import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { 
  Card, 
  CardHeader, 
  CardBody, 
  CardFooter, 
  Button, 
  OutlineButton, 
  Input, 
  Select, 
  FormGroup, 
  Label, 
  Grid, 
  FlexContainer 
} from '../../styles/StyledComponents';
import { useAuth } from '../../context/AuthContext';
import { ReportType, ReportFormat } from '../../types';
import apiService from '../../services/api';
import { exportToExcel, exportToPDF, formatDataForExport } from '../../utils/reportExportUtils';

// Define a custom interface for the form parameters since we're using string dates instead of Date objects
interface FormReportParameters {
  dateRange: {
    startDate: string;
    endDate: string;
  };
  projectIds?: string[];
  departments?: string[];
  status?: string[];
  category?: string[];
  userId?: string;
  customFilters?: Record<string, any>;
}

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const FormContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const SectionTitle = styled.h3`
  color: var(--secondary-color);
  border-bottom: 2px solid var(--light-gray);
  padding-bottom: 0.5rem;
  margin: 1.5rem 0 1rem;
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const CheckboxItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const DateRangeContainer = styled.div`
  display: flex;
  gap: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const PreviewContainer = styled.div`
  margin-top: 2rem;
  padding: 1rem;
  background-color: var(--light-gray);
  border-radius: var(--border-radius);
`;

const PreviewPlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  background-color: white;
  border-radius: var(--border-radius);
  text-align: center;
  
  h3 {
    margin-bottom: 1rem;
  }
  
  p {
    color: var(--dark-gray);
    margin-bottom: 2rem;
  }
`;

const formatReportType = (type: string): string => {
  // Convert kebab-case to readable format
  return type
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const ReportGenerator: React.FC = () => {
  const { reportType } = useParams<{ reportType: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [projects, setProjects] = useState<any[]>([]);
  const [fundRequests, setFundRequests] = useState<any[]>([]);
  const [procurementRecords, setProcurementRecords] = useState<any[]>([]);
  const [payments, setPayments] = useState<any[]>([]);
  const [expenses, setExpenses] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  const formattedReportType = formatReportType(reportType || '');
  
  // Check if the report type is valid
  const isValidReportType = (type: string): boolean => {
    const validTypes: ReportType[] = [
      'Project Reports',
      'Consolidated Project Reports',
      'Department Report',
      'Overall Company Report',
      'Financial Report',
      'Procurement Report',
      'Fund Request Report'
    ];
    
    return validTypes.includes(formattedReportType as ReportType);
  };
  
  const [formData, setFormData] = useState<{
    reportName: string;
    format: ReportFormat;
    parameters: FormReportParameters;
    showPreview: boolean;
  }>({
    reportName: `${formattedReportType} - ${new Date().toLocaleDateString()}`,
    format: 'Table',
    parameters: {
      dateRange: {
        startDate: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0]
      },
      projectIds: [],
      departments: [],
      status: [],
      category: []
    },
    showPreview: false
  });

  const [generatedReportData, setGeneratedReportData] = useState<any>(null);
  
  useEffect(() => {
    // Redirect if report type is invalid
    if (!isValidReportType(formattedReportType)) {
      navigate('/reports');
    }
  }, [formattedReportType, navigate]);

  // Load data for report generation
  useEffect(() => {
    const loadReportData = async () => {
      try {
        setIsLoading(true);
        
        // Load projects
        const projectsResponse = await apiService.getProjects({ limit: 1000 });
        setProjects(projectsResponse.data);
        
        // Load fund requests
        const fundRequestsResponse = await apiService.getFundRequests({ limit: 1000 });
        setFundRequests(fundRequestsResponse.data);
        
        // Load payments for financial reports
        const paymentsResponse = await apiService.getPayments({ limit: 1000 });
        setPayments(paymentsResponse.data);
        
        // Load expenses for procurement reports
        const expensesResponse = await apiService.getExpenses({ limit: 1000 });
        setExpenses(expensesResponse.data);
        
        // For now, use expenses as procurement records since we don't have a separate procurement model
        setProcurementRecords(expensesResponse.data || []);
        
        // Load users for company reports
        const usersResponse = await apiService.getUsers({ limit: 1000 });
        setUsers(usersResponse.data || []);
        
      } catch (error) {
        console.error('Error loading report data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    if (user) {
      loadReportData();
    }
  }, [user]);
  
  // Check if user has permission to access this report
  const checkPermission = (): boolean => {
    if (!user) return false;
    
    const rolePermissions: Record<string, string[]> = {
      'Project Reports': ['MD', 'TD', 'Project Manager'],
      'Consolidated Project Reports': ['MD', 'TD', 'Project Manager'],
      'Department Report': ['MD', 'TD', 'HR (Admin)'],
      'Overall Company Report': ['MD', 'TD'],
      'Financial Report': ['MD', 'TD', 'Accountant'],
              'Procurement Report': ['MD', 'TD', 'Store-Keeper (Logistics)', 'Procurement', 'Project Manager'],
              'Fund Request Report': ['MD', 'TD', 'Accountant', 'Project Manager']
    };
    
    const allowedRoles = rolePermissions[formattedReportType as ReportType] || [];
    return allowedRoles.some(role => user.role.includes(role));
  };
  
  if (!checkPermission()) {
    return (
      <Card>
        <CardBody>
          <p>You don't have permission to access this report.</p>
          <Link to="/reports">Back to Reports</Link>
        </CardBody>
      </Card>
    );
  }
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleDateRangeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      parameters: {
        ...prev.parameters,
        dateRange: {
          ...prev.parameters.dateRange,
          [name]: value
        }
      }
    }));
  };
  
  const handleCheckboxChange = (category: keyof FormReportParameters, value: string) => {
    setFormData(prev => {
      const currentValues = prev.parameters[category] as string[] || [];
      const newValues = currentValues.includes(value)
        ? currentValues.filter(v => v !== value)
        : [...currentValues, value];
      
      return {
        ...prev,
        parameters: {
          ...prev.parameters,
          [category]: newValues
        }
      };
    });
  };
  
  const generateReport = async () => {
    try {
      // Call real API to generate the report
      const response = await apiService.generateReport({
        type: formattedReportType,
        parameters: formData.parameters,
        format: formData.format
      });
      
      // Store the generated report data
      if (response.success) {
        setGeneratedReportData(response.data);
      }
      
      // Show preview with real data
      setFormData(prev => ({ ...prev, showPreview: true }));
    } catch (error) {
      console.error('Error generating report:', error);
      // Fallback to preview mode if API fails
      setFormData(prev => ({ ...prev, showPreview: true }));
    }
  };

  const downloadReport = async (format: 'excel' | 'pdf') => {
    try {
      if (!generatedReportData) {
        alert('Please generate a report first before downloading.');
        return;
      }

      // Format data for export
      const exportData = formatDataForExport(generatedReportData, formattedReportType);
      
      if (format === 'excel') {
        exportToExcel(exportData, `${formattedReportType.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}`);
      } else if (format === 'pdf') {
        exportToPDF(exportData, `${formattedReportType.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}`, formattedReportType);
      }
    } catch (error) {
      console.error(`Error downloading ${format} report:`, error);
      alert(`Failed to download ${format.toUpperCase()} report. Please try again.`);
    }
  };
  
  const getProjectOptions = () => {
    // Filter projects based on user role
    let availableProjects = projects;
    
    // For Project Manager, only show assigned projects
    if (user?.role === 'Project Manager') {
      availableProjects = projects.filter((project: any) => 
        project.assignedStaff?.some((staff: any) => staff.userId === user.id)
      );
    }
    
    return availableProjects.map((project: any) => ({
      value: project.id,
      label: `${project.id} - ${project.name}`
    }));
  };
  
  const getDepartmentOptions = () => {
    return [
      { value: 'Management', label: 'Management' },
      { value: 'Project Management', label: 'Project Management' },
      { value: 'Finance', label: 'Finance' },
      { value: 'Operations', label: 'Operations' },
      { value: 'Project Coordination', label: 'Project Coordination' },
      { value: 'Logistics', label: 'Logistics' },
      { value: 'HR', label: 'HR' }
    ];
  };
  
  const getStatusOptions = () => {
    switch (formattedReportType) {
      case 'Project Reports':
      case 'Consolidated Project Reports':
        return [
          { value: 'Draft', label: 'Draft' },
          { value: 'In-Progress', label: 'In-Progress' },
          { value: 'Completed', label: 'Completed' },
          { value: 'On-Hold', label: 'On-Hold' },
          { value: 'Cancelled', label: 'Cancelled' }
        ];
      case 'Fund Request Report':
        return [
          { value: 'Draft', label: 'Draft' },
          { value: 'Submitted', label: 'Submitted' },
          { value: 'Under Review', label: 'Under Review' },
          { value: 'Approved', label: 'Approved' },
          { value: 'Rejected', label: 'Rejected' },
          { value: 'Partially Approved', label: 'Partially Approved' },
          { value: 'Paid', label: 'Paid' },
          { value: 'Cancelled', label: 'Cancelled' }
        ];
      case 'Procurement Report':
        return [
          { value: 'Pending', label: 'Pending' },
          { value: 'Approved', label: 'Approved' },
          { value: 'Completed', label: 'Completed' },
          { value: 'Cancelled', label: 'Cancelled' }
        ];
      default:
        return [];
    }
  };
  
  const getCategoryOptions = () => {
    switch (formattedReportType) {
      case 'Project Reports':
      case 'Consolidated Project Reports':
      case 'Financial Report':
        return [
          { value: 'Materials', label: 'Materials' },
          { value: 'Labor', label: 'Labor' },
          { value: 'Equipment', label: 'Equipment' },
          { value: 'Sub-Contracts', label: 'Sub-Contracts' },
          { value: 'Others', label: 'Others' }
        ];
      case 'Procurement Report':
        return [
          { value: 'Goods', label: 'Goods' },
          { value: 'Services', label: 'Services' },
          { value: 'Works', label: 'Works' }
        ];
      default:
        return [];
    }
  };
  
  const renderReportPreview = () => {
    if (!formData.showPreview) return null;
    
    return (
      <PreviewContainer>
        <SectionTitle>Report Preview</SectionTitle>
        <Card>
          <CardHeader>
            <FlexContainer justify="space-between" align="center">
              <h3>{formData.reportName}</h3>
              <FlexContainer gap="0.5rem">
                <OutlineButton onClick={() => downloadReport('excel')}>
                  📊 Excel
                </OutlineButton>
                <OutlineButton onClick={() => downloadReport('pdf')}>
                  📄 PDF
                </OutlineButton>
                <Button onClick={() => window.print()}>
                  🖨️ Print
                </Button>
              </FlexContainer>
            </FlexContainer>
          </CardHeader>
          <CardBody>
            {renderReportContent()}
          </CardBody>
        </Card>
      </PreviewContainer>
    );
  };

  const renderReportContent = () => {
    // For Project Manager (Hamadi Simba), filter data to only show assigned projects
    const filterDataForProjectManager = () => {
      if (user?.role === 'Project Manager') {
        // Get assigned projects
            const assignedProjects = projects.filter((project: any) =>
      project.assignedStaff?.some((staff: any) => staff.userId === user.id)
    );
        
        // Update project IDs if none are selected
        if (!formData.parameters.projectIds || formData.parameters.projectIds.length === 0) {
          setFormData(prev => ({
            ...prev,
            parameters: {
              ...prev.parameters,
              projectIds: assignedProjects.map(p => p.id)
            }
          }));
        }
      }
    };
    
    // Apply filters before rendering report
    filterDataForProjectManager();
    
    switch (formattedReportType) {
      case 'Project Reports':
        return renderProjectReport();
      case 'Consolidated Project Reports':
        return renderConsolidatedProjectReport();
      case 'Financial Report':
        return renderFinancialReport();
      case 'Procurement Report':
        return renderProcurementReport();
      case 'Fund Request Report':
        return renderFundRequestReport();
      case 'Department Report':
        return renderDepartmentReport();
      case 'Overall Company Report':
        return renderCompanyReport();
      default:
        return <p>Report content not available</p>;
    }
  };

  const renderProjectReport = () => {
    // Calculate real statistics from loaded data
    const totalBudget = projects.reduce((sum, project) => sum + (project.budget?.total || 0), 0);
    const totalSpent = expenses.reduce((sum, expense) => sum + (expense.amount || 0), 0);
    const remaining = totalBudget - totalSpent;

    // Calculate budget breakdown by category
    const budgetBreakdown = projects.reduce((acc, project) => {
      if (project.budget) {
        acc.materials = (acc.materials || 0) + (project.budget.materials || 0);
        acc.labor = (acc.labor || 0) + (project.budget.labor || 0);
        acc.equipment = (acc.equipment || 0) + (project.budget.equipment || 0);
        acc.subContracts = (acc.subContracts || 0) + (project.budget.subContracts || 0);
        acc.others = (acc.others || 0) + (project.budget.others || 0);
      }
      return acc;
    }, {} as any);

    // Calculate spent by category
    const spentByCategory = expenses.reduce((acc, expense) => {
      acc[expense.category] = (acc[expense.category] || 0) + (expense.amount || 0);
      return acc;
    }, {} as any);

    const formatCurrency = (amount: number) => {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'TZS',
        maximumFractionDigits: 0
      }).format(amount);
    };

    const formatDate = (date: string | Date) => {
      return new Date(date).toLocaleDateString('en-US');
    };

    return (
      <div>
        <Grid columns="1fr 1fr 1fr" gap="1rem" style={{ marginBottom: '2rem' }}>
          <Card style={{ padding: '1rem', textAlign: 'center' }}>
            <h4 style={{ color: '#17a2b8', margin: '0 0 0.5rem 0' }}>Total Budget</h4>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{formatCurrency(totalBudget)}</div>
          </Card>
          <Card style={{ padding: '1rem', textAlign: 'center' }}>
            <h4 style={{ color: '#28a745', margin: '0 0 0.5rem 0' }}>Spent</h4>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{formatCurrency(totalSpent)}</div>
          </Card>
          <Card style={{ padding: '1rem', textAlign: 'center' }}>
            <h4 style={{ color: '#ffc107', margin: '0 0 0.5rem 0' }}>Remaining</h4>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{formatCurrency(remaining)}</div>
          </Card>
        </Grid>
        
        <h4>Budget Breakdown by Category</h4>
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '2rem' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa' }}>
              <th style={{ padding: '0.75rem', textAlign: 'left', border: '1px solid #dee2e6' }}>Category</th>
              <th style={{ padding: '0.75rem', textAlign: 'right', border: '1px solid #dee2e6' }}>Budgeted</th>
              <th style={{ padding: '0.75rem', textAlign: 'right', border: '1px solid #dee2e6' }}>Spent</th>
              <th style={{ padding: '0.75rem', textAlign: 'right', border: '1px solid #dee2e6' }}>Remaining</th>
              <th style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #dee2e6' }}>% Used</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(budgetBreakdown).map(([category, budgeted]) => {
              const spent = spentByCategory[category] || 0;
              const remaining = (budgeted as number) - spent;
              const percentUsed = (budgeted as number) > 0 ? Math.round((spent / (budgeted as number)) * 100) : 0;
              
              return (
                <tr key={category}>
                  <td style={{ padding: '0.75rem', border: '1px solid #dee2e6' }}>{category}</td>
                  <td style={{ padding: '0.75rem', textAlign: 'right', border: '1px solid #dee2e6' }}>{formatCurrency(budgeted as number)}</td>
                  <td style={{ padding: '0.75rem', textAlign: 'right', border: '1px solid #dee2e6' }}>{formatCurrency(spent)}</td>
                  <td style={{ padding: '0.75rem', textAlign: 'right', border: '1px solid #dee2e6' }}>{formatCurrency(remaining)}</td>
                  <td style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #dee2e6' }}>{percentUsed}%</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        
        <h4>Recent Fund Requests</h4>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa' }}>
              <th style={{ padding: '0.75rem', textAlign: 'left', border: '1px solid #dee2e6' }}>Request ID</th>
              <th style={{ padding: '0.75rem', textAlign: 'left', border: '1px solid #dee2e6' }}>Requester</th>
              <th style={{ padding: '0.75rem', textAlign: 'right', border: '1px solid #dee2e6' }}>Amount</th>
              <th style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #dee2e6' }}>Status</th>
              <th style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #dee2e6' }}>Date</th>
            </tr>
          </thead>
          <tbody>
            {fundRequests.slice(0, 5).map((request) => (
              <tr key={request.id || request._id}>
                <td style={{ padding: '0.75rem', border: '1px solid #dee2e6' }}>{request.id || request._id}</td>
                <td style={{ padding: '0.75rem', border: '1px solid #dee2e6' }}>{request.requester}</td>
                <td style={{ padding: '0.75rem', textAlign: 'right', border: '1px solid #dee2e6' }}>{formatCurrency(request.amount)}</td>
                <td style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #dee2e6' }}>
                  {request.status === 'Approved' ? '✅ Approved' : 
                   request.status === 'Pending' ? '⏳ Pending' : 
                   request.status === 'Paid' ? '💰 Paid' : 
                   request.status === 'Rejected' ? '❌ Rejected' : request.status}
                </td>
                <td style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #dee2e6' }}>{formatDate(request.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderFinancialReport = () => {
    // Calculate real financial statistics
    const totalRevenue = payments.reduce((sum, payment) => sum + (payment.amount || 0), 0);
    const totalExpenses = expenses.reduce((sum, expense) => sum + (expense.amount || 0), 0);
    const netProfit = totalRevenue - totalExpenses;
    const profitMargin = totalRevenue > 0 ? (netProfit / totalRevenue) * 100 : 0;

    // Group expenses by month
    const expensesByMonth = expenses.reduce((acc, expense) => {
      const month = new Date(expense.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
      if (!acc[month]) {
        acc[month] = { expenses: 0, revenue: 0 };
      }
      acc[month].expenses += expense.amount || 0;
      return acc;
    }, {} as any);

    // Group payments by month
    const paymentsByMonth = payments.reduce((acc, payment) => {
      const month = new Date(payment.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
      if (!acc[month]) {
        acc[month] = { expenses: 0, revenue: 0 };
      }
      acc[month].revenue += payment.amount || 0;
      return acc;
    }, {} as any);

    // Merge and calculate monthly data
    const monthlyData = Object.keys({ ...expensesByMonth, ...paymentsByMonth }).map(month => {
      const expenses = expensesByMonth[month]?.expenses || 0;
      const revenue = paymentsByMonth[month]?.revenue || 0;
      const netProfit = revenue - expenses;
      const margin = revenue > 0 ? (netProfit / revenue) * 100 : 0;
      
      return { month, revenue, expenses, netProfit, margin };
    }).sort((a, b) => new Date(a.month).getTime() - new Date(b.month).getTime());

    const formatCurrency = (amount: number) => {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'TZS',
        maximumFractionDigits: 0
      }).format(amount);
    };

    return (
      <div>
        <Grid columns="1fr 1fr 1fr 1fr" gap="1rem" style={{ marginBottom: '2rem' }}>
          <Card style={{ padding: '1rem', textAlign: 'center' }}>
            <h4 style={{ color: '#28a745', margin: '0 0 0.5rem 0' }}>Total Revenue</h4>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{formatCurrency(totalRevenue)}</div>
          </Card>
          <Card style={{ padding: '1rem', textAlign: 'center' }}>
            <h4 style={{ color: '#dc3545', margin: '0 0 0.5rem 0' }}>Total Expenses</h4>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{formatCurrency(totalExpenses)}</div>
          </Card>
          <Card style={{ padding: '1rem', textAlign: 'center' }}>
            <h4 style={{ color: '#17a2b8', margin: '0 0 0.5rem 0' }}>Net Profit</h4>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{formatCurrency(netProfit)}</div>
          </Card>
          <Card style={{ padding: '1rem', textAlign: 'center' }}>
            <h4 style={{ color: '#ffc107', margin: '0 0 0.5rem 0' }}>Profit Margin</h4>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{profitMargin.toFixed(1)}%</div>
          </Card>
        </Grid>

        <h4>Monthly Financial Summary</h4>
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '2rem' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa' }}>
              <th style={{ padding: '0.75rem', textAlign: 'left', border: '1px solid #dee2e6' }}>Month</th>
              <th style={{ padding: '0.75rem', textAlign: 'right', border: '1px solid #dee2e6' }}>Revenue</th>
              <th style={{ padding: '0.75rem', textAlign: 'right', border: '1px solid #dee2e6' }}>Expenses</th>
              <th style={{ padding: '0.75rem', textAlign: 'right', border: '1px solid #dee2e6' }}>Net Profit</th>
              <th style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #dee2e6' }}>Margin</th>
            </tr>
          </thead>
          <tbody>
            {monthlyData.map((data) => (
              <tr key={data.month}>
                <td style={{ padding: '0.75rem', border: '1px solid #dee2e6' }}>{data.month}</td>
                <td style={{ padding: '0.75rem', textAlign: 'right', border: '1px solid #dee2e6' }}>{formatCurrency(data.revenue)}</td>
                <td style={{ padding: '0.75rem', textAlign: 'right', border: '1px solid #dee2e6' }}>{formatCurrency(data.expenses)}</td>
                <td style={{ padding: '0.75rem', textAlign: 'right', border: '1px solid #dee2e6' }}>{formatCurrency(data.netProfit)}</td>
                <td style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #dee2e6' }}>{data.margin.toFixed(1)}%</td>
              </tr>
            ))}
          </tbody>
        </table>

        <h4>Cash Flow Analysis</h4>
        <Grid columns="1fr 1fr" gap="2rem">
          <Card style={{ padding: '1rem' }}>
            <h5>Cash Inflows</h5>
            <div style={{ marginBottom: '0.5rem' }}>Project Payments: {formatCurrency(totalRevenue)}</div>
            <div style={{ fontWeight: 'bold', borderTop: '1px solid #dee2e6', paddingTop: '0.5rem' }}>
              Total Inflows: {formatCurrency(totalRevenue)}
            </div>
          </Card>
          <Card style={{ padding: '1rem' }}>
            <h5>Cash Outflows</h5>
            {Object.entries(expenses.reduce((acc, expense) => {
              acc[expense.category] = (acc[expense.category] || 0) + (expense.amount || 0);
              return acc;
            }, {} as any)).map(([category, amount]) => (
              <div key={category} style={{ marginBottom: '0.5rem' }}>
                {category}: {formatCurrency(amount as number)}
              </div>
            ))}
            <div style={{ fontWeight: 'bold', borderTop: '1px solid #dee2e6', paddingTop: '0.5rem' }}>
              Total Outflows: {formatCurrency(totalExpenses)}
            </div>
          </Card>
        </Grid>
      </div>
    );
  };

  const renderProcurementReport = () => {
    // Calculate real procurement statistics
    const totalProcurements = expenses.length;
    const completedProcurements = expenses.filter(expense => expense.status === 'approved' || expense.status === 'paid').length;
    const pendingProcurements = expenses.filter(expense => expense.status === 'pending').length;
    const totalAmount = expenses.reduce((sum, expense) => sum + (expense.amount || 0), 0);

    // Group by supplier
    const supplierStats = expenses.reduce((acc, expense) => {
      const supplier = expense.supplier || 'Unknown Supplier';
      if (!acc[supplier]) {
        acc[supplier] = { count: 0, totalAmount: 0 };
      }
      acc[supplier].count += 1;
      acc[supplier].totalAmount += expense.amount || 0;
      return acc;
    }, {} as any);

    // Group by category
    const categoryStats = expenses.reduce((acc, expense) => {
      const category = expense.category || 'Others';
      if (!acc[category]) {
        acc[category] = { count: 0, totalAmount: 0 };
      }
      acc[category].count += 1;
      acc[category].totalAmount += expense.amount || 0;
      return acc;
    }, {} as any);

    const formatCurrency = (amount: number) => {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'TZS',
        maximumFractionDigits: 0
      }).format(amount);
    };

    return (
      <div>
        <Grid columns="1fr 1fr 1fr" gap="1rem" style={{ marginBottom: '2rem' }}>
          <Card style={{ padding: '1rem', textAlign: 'center' }}>
            <h4 style={{ color: '#17a2b8', margin: '0 0 0.5rem 0' }}>Total Procurements</h4>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{totalProcurements}</div>
          </Card>
          <Card style={{ padding: '1rem', textAlign: 'center' }}>
            <h4 style={{ color: '#28a745', margin: '0 0 0.5rem 0' }}>Completed</h4>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{completedProcurements}</div>
          </Card>
          <Card style={{ padding: '1rem', textAlign: 'center' }}>
            <h4 style={{ color: '#ffc107', margin: '0 0 0.5rem 0' }}>Pending</h4>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{pendingProcurements}</div>
          </Card>
        </Grid>

        <h4>Top Suppliers</h4>
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '2rem' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa' }}>
              <th style={{ padding: '0.75rem', textAlign: 'left', border: '1px solid #dee2e6' }}>Supplier</th>
              <th style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #dee2e6' }}>Orders</th>
              <th style={{ padding: '0.75rem', textAlign: 'right', border: '1px solid #dee2e6' }}>Total Amount</th>
              <th style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #dee2e6' }}>Rating</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(supplierStats)
              .sort(([,a], [,b]) => (b as any).totalAmount - (a as any).totalAmount)
              .slice(0, 5)
              .map(([supplier, stats]) => (
                <tr key={supplier}>
                  <td style={{ padding: '0.75rem', border: '1px solid #dee2e6' }}>{supplier}</td>
                  <td style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #dee2e6' }}>{(stats as any).count}</td>
                  <td style={{ padding: '0.75rem', textAlign: 'right', border: '1px solid #dee2e6' }}>{formatCurrency((stats as any).totalAmount)}</td>
                  <td style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #dee2e6' }}>⭐⭐⭐⭐</td>
                </tr>
              ))}
          </tbody>
        </table>

        <h4>Procurement by Category</h4>
        <Grid columns="1fr 1fr" gap="2rem">
          <Card style={{ padding: '1rem' }}>
            <h5>Category Breakdown</h5>
            {Object.entries(categoryStats).map(([category, stats]) => (
              <div key={category} style={{ marginBottom: '0.5rem' }}>
                {category}: {formatCurrency((stats as any).totalAmount)} ({(stats as any).count} orders)
              </div>
            ))}
            <div style={{ fontWeight: 'bold', borderTop: '1px solid #dee2e6', paddingTop: '0.5rem' }}>
              Total: {formatCurrency(totalAmount)}
            </div>
          </Card>
          <Card style={{ padding: '1rem' }}>
            <h5>Summary</h5>
            <div style={{ marginBottom: '0.5rem' }}>Total Orders: {totalProcurements}</div>
            <div style={{ marginBottom: '0.5rem' }}>Completed: {completedProcurements}</div>
            <div style={{ marginBottom: '0.5rem' }}>Pending: {pendingProcurements}</div>
            <div style={{ marginBottom: '0.5rem' }}>Average Order Value: {formatCurrency(totalAmount / Math.max(1, totalProcurements))}</div>
            <div style={{ fontWeight: 'bold', borderTop: '1px solid #dee2e6', paddingTop: '0.5rem' }}>
              Completion Rate: {totalProcurements > 0 ? Math.round((completedProcurements / totalProcurements) * 100) : 0}%
            </div>
          </Card>
        </Grid>
      </div>
    );
  };

  const renderFundRequestReport = () => {
    // Calculate real fund request statistics
    const totalRequests = fundRequests.length;
    const approvedRequests = fundRequests.filter(request => request.status === 'Approved' || request.status === 'Paid').length;
    const pendingRequests = fundRequests.filter(request => request.status === 'Pending' || request.status === 'Under Review').length;
    const rejectedRequests = fundRequests.filter(request => request.status === 'Rejected').length;
    const totalAmount = fundRequests.reduce((sum, request) => sum + (request.amount || 0), 0);

    // Group by requester
    const requesterStats = fundRequests.reduce((acc, request) => {
      const requester = request.requester || 'Unknown';
      if (!acc[requester]) {
        acc[requester] = { count: 0, totalAmount: 0, approved: 0 };
      }
      acc[requester].count += 1;
      acc[requester].totalAmount += request.amount || 0;
      if (request.status === 'Approved' || request.status === 'Paid') {
        acc[requester].approved += 1;
      }
      return acc;
    }, {} as any);

    // Calculate approval timeline statistics
    const approvalStats = fundRequests.reduce((acc, request) => {
      if (request.approvalChain && request.approvalChain.length > 0) {
        request.approvalChain.forEach((step: any, index: number) => {
          if (!acc[step.role]) {
            acc[step.role] = { total: 0, approved: 0, avgTime: 0 };
          }
          acc[step.role].total += 1;
          if (step.status === 'Approved') {
            acc[step.role].approved += 1;
          }
        });
      }
      return acc;
    }, {} as any);

    const formatCurrency = (amount: number) => {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'TZS',
        maximumFractionDigits: 0
      }).format(amount);
    };

    return (
      <div>
        <Grid columns="1fr 1fr 1fr 1fr" gap="1rem" style={{ marginBottom: '2rem' }}>
          <Card style={{ padding: '1rem', textAlign: 'center' }}>
            <h4 style={{ color: '#17a2b8', margin: '0 0 0.5rem 0' }}>Total Requests</h4>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{totalRequests}</div>
          </Card>
          <Card style={{ padding: '1rem', textAlign: 'center' }}>
            <h4 style={{ color: '#28a745', margin: '0 0 0.5rem 0' }}>Approved</h4>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{approvedRequests}</div>
          </Card>
          <Card style={{ padding: '1rem', textAlign: 'center' }}>
            <h4 style={{ color: '#ffc107', margin: '0 0 0.5rem 0' }}>Under Review</h4>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{pendingRequests}</div>
          </Card>
          <Card style={{ padding: '1rem', textAlign: 'center' }}>
            <h4 style={{ color: '#dc3545', margin: '0 0 0.5rem 0' }}>Rejected</h4>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{rejectedRequests}</div>
          </Card>
        </Grid>

        <h4>Approval Timeline Analysis</h4>
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '2rem' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa' }}>
              <th style={{ padding: '0.75rem', textAlign: 'left', border: '1px solid #dee2e6' }}>Approval Stage</th>
              <th style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #dee2e6' }}>Requests</th>
              <th style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #dee2e6' }}>Approved</th>
              <th style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #dee2e6' }}>Success Rate</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(approvalStats).map(([role, stats]) => {
              const successRate = (stats as any).total > 0 ? Math.round(((stats as any).approved / (stats as any).total) * 100) : 0;
              return (
                <tr key={role}>
                  <td style={{ padding: '0.75rem', border: '1px solid #dee2e6' }}>{role} Review</td>
                  <td style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #dee2e6' }}>{(stats as any).total}</td>
                  <td style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #dee2e6' }}>{(stats as any).approved}</td>
                  <td style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #dee2e6' }}>{successRate}%</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <h4>Top Requesters</h4>
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '2rem' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa' }}>
              <th style={{ padding: '0.75rem', textAlign: 'left', border: '1px solid #dee2e6' }}>Requester</th>
              <th style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #dee2e6' }}>Requests</th>
              <th style={{ padding: '0.75rem', textAlign: 'right', border: '1px solid #dee2e6' }}>Total Amount</th>
              <th style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #dee2e6' }}>Approval Rate</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(requesterStats)
              .sort(([,a], [,b]) => (b as any).totalAmount - (a as any).totalAmount)
              .slice(0, 5)
              .map(([requester, stats]) => {
                const approvalRate = (stats as any).count > 0 ? Math.round(((stats as any).approved / (stats as any).count) * 100) : 0;
                return (
                  <tr key={requester}>
                    <td style={{ padding: '0.75rem', border: '1px solid #dee2e6' }}>{requester}</td>
                    <td style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #dee2e6' }}>{(stats as any).count}</td>
                    <td style={{ padding: '0.75rem', textAlign: 'right', border: '1px solid #dee2e6' }}>{formatCurrency((stats as any).totalAmount)}</td>
                    <td style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #dee2e6' }}>{approvalRate}%</td>
                  </tr>
                );
              })}
          </tbody>
        </table>

        <Grid columns="1fr 1fr" gap="2rem">
          <Card style={{ padding: '1rem' }}>
            <h5>Summary Statistics</h5>
            <div style={{ marginBottom: '0.5rem' }}>Total Requests: {totalRequests}</div>
            <div style={{ marginBottom: '0.5rem' }}>Total Amount: {formatCurrency(totalAmount)}</div>
            <div style={{ marginBottom: '0.5rem' }}>Average Request: {formatCurrency(totalAmount / Math.max(1, totalRequests))}</div>
            <div style={{ marginBottom: '0.5rem' }}>Approval Rate: {totalRequests > 0 ? Math.round((approvedRequests / totalRequests) * 100) : 0}%</div>
          </Card>
          <Card style={{ padding: '1rem' }}>
            <h5>Status Distribution</h5>
            <div style={{ marginBottom: '0.5rem' }}>Approved: {approvedRequests} ({totalRequests > 0 ? Math.round((approvedRequests / totalRequests) * 100) : 0}%)</div>
            <div style={{ marginBottom: '0.5rem' }}>Pending: {pendingRequests} ({totalRequests > 0 ? Math.round((pendingRequests / totalRequests) * 100) : 0}%)</div>
            <div style={{ marginBottom: '0.5rem' }}>Rejected: {rejectedRequests} ({totalRequests > 0 ? Math.round((rejectedRequests / totalRequests) * 100) : 0}%)</div>
          </Card>
        </Grid>
      </div>
    );
  };

  const renderDepartmentReport = () => (
    <div>
      <Grid columns="1fr 1fr 1fr" gap="1rem" style={{ marginBottom: '2rem' }}>
        <Card style={{ padding: '1rem', textAlign: 'center' }}>
          <h4 style={{ color: '#17a2b8', margin: '0 0 0.5rem 0' }}>Total Staff</h4>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>47</div>
        </Card>
        <Card style={{ padding: '1rem', textAlign: 'center' }}>
          <h4 style={{ color: '#28a745', margin: '0 0 0.5rem 0' }}>Active Projects</h4>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>12</div>
        </Card>
        <Card style={{ padding: '1rem', textAlign: 'center' }}>
          <h4 style={{ color: '#ffc107', margin: '0 0 0.5rem 0' }}>Departments</h4>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>7</div>
        </Card>
      </Grid>

      <h4>Department Performance</h4>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '2rem' }}>
        <thead>
          <tr style={{ backgroundColor: '#f8f9fa' }}>
            <th style={{ padding: '0.75rem', textAlign: 'left', border: '1px solid #dee2e6' }}>Department</th>
            <th style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #dee2e6' }}>Staff Count</th>
            <th style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #dee2e6' }}>Active Projects</th>
            <th style={{ padding: '0.75rem', textAlign: 'right', border: '1px solid #dee2e6' }}>Budget Utilization</th>
            <th style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #dee2e6' }}>Efficiency</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={{ padding: '0.75rem', border: '1px solid #dee2e6' }}>Project Management</td>
            <td style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #dee2e6' }}>8</td>
            <td style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #dee2e6' }}>12</td>
            <td style={{ padding: '0.75rem', textAlign: 'right', border: '1px solid #dee2e6' }}>87%</td>
            <td style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #dee2e6' }}>⭐⭐⭐⭐⭐</td>
          </tr>
          <tr>
            <td style={{ padding: '0.75rem', border: '1px solid #dee2e6' }}>Finance</td>
            <td style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #dee2e6' }}>6</td>
            <td style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #dee2e6' }}>12</td>
            <td style={{ padding: '0.75rem', textAlign: 'right', border: '1px solid #dee2e6' }}>92%</td>
            <td style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #dee2e6' }}>⭐⭐⭐⭐⭐</td>
          </tr>
          <tr>
            <td style={{ padding: '0.75rem', border: '1px solid #dee2e6' }}>Operations</td>
            <td style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #dee2e6' }}>15</td>
            <td style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #dee2e6' }}>8</td>
            <td style={{ padding: '0.75rem', textAlign: 'right', border: '1px solid #dee2e6' }}>78%</td>
            <td style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #dee2e6' }}>⭐⭐⭐⭐</td>
          </tr>
          <tr>
            <td style={{ padding: '0.75rem', border: '1px solid #dee2e6' }}>Logistics</td>
            <td style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #dee2e6' }}>12</td>
            <td style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #dee2e6' }}>10</td>
            <td style={{ padding: '0.75rem', textAlign: 'right', border: '1px solid #dee2e6' }}>85%</td>
            <td style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #dee2e6' }}>⭐⭐⭐⭐</td>
          </tr>
        </tbody>
      </table>
    </div>
  );

  const renderConsolidatedProjectReport = () => {
    // Calculate real statistics from projects data
    const totalProjects = projects.length;
    const completedProjects = projects.filter(project => project.status === 'Completed').length;
    const inProgressProjects = projects.filter(project => project.status === 'In-Progress').length;
    const onHoldProjects = projects.filter(project => project.status === 'On-Hold').length;
    
    // Group projects by type
    const projectsByType = projects.reduce((acc, project) => {
      const type = project.type || 'Other';
      if (!acc[type]) {
        acc[type] = { count: 0, totalBudget: 0, totalSpent: 0 };
      }
      acc[type].count += 1;
      acc[type].totalBudget += project.budget?.total || 0;
      
      // Calculate spent amount from expenses for this project
      const projectExpenses = expenses.filter(expense => expense.projectId === project._id || expense.projectId === project.id);
      const projectSpent = projectExpenses.reduce((sum, expense) => sum + (expense.amount || 0), 0);
      acc[type].totalSpent += projectSpent;
      
      return acc;
    }, {} as any);

    const formatCurrency = (amount: number) => {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'TZS',
        maximumFractionDigits: 0
      }).format(amount);
    };

    return (
      <div>
        <Grid columns="1fr 1fr 1fr 1fr" gap="1rem" style={{ marginBottom: '2rem' }}>
          <Card style={{ padding: '1rem', textAlign: 'center' }}>
            <h4 style={{ color: '#17a2b8', margin: '0 0 0.5rem 0' }}>Total Projects</h4>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{totalProjects}</div>
          </Card>
          <Card style={{ padding: '1rem', textAlign: 'center' }}>
            <h4 style={{ color: '#28a745', margin: '0 0 0.5rem 0' }}>Completed</h4>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{completedProjects}</div>
          </Card>
          <Card style={{ padding: '1rem', textAlign: 'center' }}>
            <h4 style={{ color: '#ffc107', margin: '0 0 0.5rem 0' }}>In Progress</h4>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{inProgressProjects}</div>
          </Card>
          <Card style={{ padding: '1rem', textAlign: 'center' }}>
            <h4 style={{ color: '#6c757d', margin: '0 0 0.5rem 0' }}>On Hold</h4>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{onHoldProjects}</div>
          </Card>
        </Grid>

        <h4>Project Portfolio Overview</h4>
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '2rem' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa' }}>
              <th style={{ padding: '0.75rem', textAlign: 'left', border: '1px solid #dee2e6' }}>Project Type</th>
              <th style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #dee2e6' }}>Count</th>
              <th style={{ padding: '0.75rem', textAlign: 'right', border: '1px solid #dee2e6' }}>Total Budget</th>
              <th style={{ padding: '0.75rem', textAlign: 'right', border: '1px solid #dee2e6' }}>Spent</th>
              <th style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #dee2e6' }}>Completion</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(projectsByType).map(([type, stats]) => {
              const completionRate = (stats as any).totalBudget > 0 ? 
                Math.round(((stats as any).totalSpent / (stats as any).totalBudget) * 100) : 0;
              
              return (
                <tr key={type}>
                  <td style={{ padding: '0.75rem', border: '1px solid #dee2e6' }}>{type}</td>
                  <td style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #dee2e6' }}>{(stats as any).count}</td>
                  <td style={{ padding: '0.75rem', textAlign: 'right', border: '1px solid #dee2e6' }}>{formatCurrency((stats as any).totalBudget)}</td>
                  <td style={{ padding: '0.75rem', textAlign: 'right', border: '1px solid #dee2e6' }}>{formatCurrency((stats as any).totalSpent)}</td>
                  <td style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #dee2e6' }}>{completionRate}%</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderCompanyReport = () => {
    // Calculate real statistics from data
    const totalProjects = projects.length;
    const totalStaff = users.length;
    const totalBudget = projects.reduce((sum, project) => sum + (project.budget?.total || 0), 0);
    const totalExpenses = expenses.reduce((sum, expense) => sum + (expense.amount || 0), 0);
    const totalFundRequests = fundRequests.length;
    const totalPayments = payments.length;
    
    // Calculate completion rate
    const completedProjects = projects.filter(project => project.status === 'Completed').length;
    const completionRate = totalProjects > 0 ? Math.round((completedProjects / totalProjects) * 100) : 0;
    
    // Calculate budget adherence
    const budgetAdherence = totalBudget > 0 ? Math.round(((totalBudget - totalExpenses) / totalBudget) * 100) : 0;
    
    const formatCurrency = (amount: number) => {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'TZS',
        maximumFractionDigits: 0
      }).format(amount);
    };

    return (
      <div>
        <Grid columns="1fr 1fr 1fr 1fr" gap="1rem" style={{ marginBottom: '2rem' }}>
          <Card style={{ padding: '1rem', textAlign: 'center' }}>
            <h4 style={{ color: '#28a745', margin: '0 0 0.5rem 0' }}>Total Budget</h4>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{formatCurrency(totalBudget)}</div>
          </Card>
          <Card style={{ padding: '1rem', textAlign: 'center' }}>
            <h4 style={{ color: '#17a2b8', margin: '0 0 0.5rem 0' }}>Active Projects</h4>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{totalProjects}</div>
          </Card>
          <Card style={{ padding: '1rem', textAlign: 'center' }}>
            <h4 style={{ color: '#ffc107', margin: '0 0 0.5rem 0' }}>Total Staff</h4>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{totalStaff}</div>
          </Card>
          <Card style={{ padding: '1rem', textAlign: 'center' }}>
            <h4 style={{ color: '#6f42c1', margin: '0 0 0.5rem 0' }}>Completion Rate</h4>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{completionRate}%</div>
          </Card>
        </Grid>

        <h4>Company Performance Metrics</h4>
        <Grid columns="1fr 1fr" gap="2rem" style={{ marginBottom: '2rem' }}>
          <Card style={{ padding: '1rem' }}>
            <h5>Financial Health</h5>
            <div style={{ marginBottom: '0.5rem' }}>Total Budget: {formatCurrency(totalBudget)}</div>
            <div style={{ marginBottom: '0.5rem' }}>Total Expenses: {formatCurrency(totalExpenses)}</div>
            <div style={{ marginBottom: '0.5rem' }}>Budget Adherence: {budgetAdherence}%</div>
            <div style={{ marginBottom: '0.5rem' }}>Total Payments: {formatCurrency(payments.reduce((sum, payment) => sum + (payment.amount || 0), 0))}</div>
          </Card>
          <Card style={{ padding: '1rem' }}>
            <h5>Operational Efficiency</h5>
            <div style={{ marginBottom: '0.5rem' }}>Project Completion Rate: {completionRate}%</div>
            <div style={{ marginBottom: '0.5rem' }}>Total Fund Requests: {totalFundRequests}</div>
            <div style={{ marginBottom: '0.5rem' }}>Budget Adherence: {budgetAdherence}%</div>
            <div style={{ marginBottom: '0.5rem' }}>Active Staff: {users.filter(user => user.status === 'Active').length}</div>
          </Card>
        </Grid>

        <h4>Key Performance Indicators</h4>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa' }}>
              <th style={{ padding: '0.75rem', textAlign: 'left', border: '1px solid #dee2e6' }}>KPI</th>
              <th style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #dee2e6' }}>Current</th>
              <th style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #dee2e6' }}>Target</th>
              <th style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #dee2e6' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ padding: '0.75rem', border: '1px solid #dee2e6' }}>Project Completion</td>
              <td style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #dee2e6' }}>{completionRate}%</td>
              <td style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #dee2e6' }}>90%</td>
              <td style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #dee2e6' }}>
                <span style={{ color: completionRate >= 90 ? '#28a745' : '#ffc107' }}>
                  {completionRate >= 90 ? '✅ On Track' : '⚠️ Needs Attention'}
                </span>
              </td>
            </tr>
            <tr>
              <td style={{ padding: '0.75rem', border: '1px solid #dee2e6' }}>Budget Adherence</td>
              <td style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #dee2e6' }}>{budgetAdherence}%</td>
              <td style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #dee2e6' }}>95%</td>
              <td style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #dee2e6' }}>
                <span style={{ color: budgetAdherence >= 95 ? '#28a745' : '#ffc107' }}>
                  {budgetAdherence >= 95 ? '✅ On Track' : '⚠️ Needs Attention'}
                </span>
              </td>
            </tr>
            <tr>
              <td style={{ padding: '0.75rem', border: '1px solid #dee2e6' }}>Staff Utilization</td>
              <td style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #dee2e6' }}>{users.filter(user => user.status === 'Active').length}/{totalStaff}</td>
              <td style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #dee2e6' }}>95%</td>
              <td style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #dee2e6' }}>
                <span style={{ color: '#28a745' }}>✅ On Track</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };
  
  return (
    <div>
      <PageHeader>
        <h1 className="page-title">{formattedReportType}</h1>
        <OutlineButton as={Link} to="/reports">
          Back to Reports
        </OutlineButton>
      </PageHeader>
      
      <FormContainer>
        <Card>
          <CardHeader>
            <h3>Configure Report</h3>
          </CardHeader>
          <CardBody>
            <Grid columns="1fr 1fr" gap="1rem">
              <FormGroup>
                <Label htmlFor="reportName">Report Name</Label>
                <Input
                  id="reportName"
                  name="reportName"
                  value={formData.reportName}
                  onChange={handleInputChange}
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="format">Report Format</Label>
                <Select
                  id="format"
                  name="format"
                  value={formData.format}
                  onChange={handleInputChange}
                >
                  <option value="Table">Table</option>
                  <option value="Summary">Summary</option>
                  <option value="Chart">Chart</option>
                </Select>
              </FormGroup>
            </Grid>
            
            <SectionTitle>Date Range</SectionTitle>
            <DateRangeContainer>
              <FormGroup>
                <Label htmlFor="startDate">Start Date</Label>
                <Input
                  id="startDate"
                  name="startDate"
                  type="date"
                  value={formData.parameters.dateRange.startDate}
                  onChange={handleDateRangeChange}
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="endDate">End Date</Label>
                <Input
                  id="endDate"
                  name="endDate"
                  type="date"
                  value={formData.parameters.dateRange.endDate}
                  onChange={handleDateRangeChange}
                />
              </FormGroup>
            </DateRangeContainer>
            
            {/* Project selection for project-related reports */}
            {['Project Reports', 'Consolidated Project Reports', 'Procurement Report', 'Fund Request Report'].includes(formattedReportType) && (
              <>
                <SectionTitle>Projects</SectionTitle>
                <CheckboxGroup>
                  {getProjectOptions().map(option => (
                    <CheckboxItem key={option.value}>
                      <input
                        type="checkbox"
                        id={`project-${option.value}`}
                        checked={formData.parameters.projectIds?.includes(option.value)}
                        onChange={() => handleCheckboxChange('projectIds', option.value)}
                      />
                      <label htmlFor={`project-${option.value}`}>{option.label}</label>
                    </CheckboxItem>
                  ))}
                </CheckboxGroup>
              </>
            )}
            
            {/* Department selection for department-related reports */}
            {['Department Report', 'Overall Company Report'].includes(formattedReportType) && (
              <>
                <SectionTitle>Departments</SectionTitle>
                <CheckboxGroup>
                  {getDepartmentOptions().map(option => (
                    <CheckboxItem key={option.value}>
                      <input
                        type="checkbox"
                        id={`department-${option.value}`}
                        checked={formData.parameters.departments?.includes(option.value)}
                        onChange={() => handleCheckboxChange('departments', option.value)}
                      />
                      <label htmlFor={`department-${option.value}`}>{option.label}</label>
                    </CheckboxItem>
                  ))}
                </CheckboxGroup>
              </>
            )}
            
            {/* Status selection */}
            {getStatusOptions().length > 0 && (
              <>
                <SectionTitle>Status</SectionTitle>
                <CheckboxGroup>
                  {getStatusOptions().map(option => (
                    <CheckboxItem key={option.value}>
                      <input
                        type="checkbox"
                        id={`status-${option.value}`}
                        checked={formData.parameters.status?.includes(option.value)}
                        onChange={() => handleCheckboxChange('status', option.value)}
                      />
                      <label htmlFor={`status-${option.value}`}>{option.label}</label>
                    </CheckboxItem>
                  ))}
                </CheckboxGroup>
              </>
            )}
            
            {/* Category selection */}
            {getCategoryOptions().length > 0 && (
              <>
                <SectionTitle>Categories</SectionTitle>
                <CheckboxGroup>
                  {getCategoryOptions().map(option => (
                    <CheckboxItem key={option.value}>
                      <input
                        type="checkbox"
                        id={`category-${option.value}`}
                        checked={formData.parameters.category?.includes(option.value)}
                        onChange={() => handleCheckboxChange('category', option.value)}
                      />
                      <label htmlFor={`category-${option.value}`}>{option.label}</label>
                    </CheckboxItem>
                  ))}
                </CheckboxGroup>
              </>
            )}
          </CardBody>
          <CardFooter>
            <FlexContainer gap="1rem" justify="flex-end">
              <OutlineButton as={Link} to="/reports">
                Cancel
              </OutlineButton>
              <Button onClick={generateReport}>
                Generate Report
              </Button>
            </FlexContainer>
          </CardFooter>
        </Card>
        
        {renderReportPreview()}
      </FormContainer>
      
      <div style={{ textAlign: 'right', padding: '1rem 0', color: 'var(--dark-gray)', fontSize: '0.8rem' }}>
        Developed by Sandfish
      </div>
    </div>
  );
};

export default ReportGenerator; 